import { createContext, useContext, useEffect, useState } from "react";
import { useMember } from "./memberContext";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { member } = useMember();
  const [theme, setThemeState] = useState<Theme>("light");

  // Initialize theme from member settings or localStorage
  useEffect(() => {
    const initializeTheme = () => {
      // Priority: member settings > localStorage > default light
      const memberTheme = member?.settings?.theme;
      const storedTheme = localStorage.getItem("theme") as Theme | null;
      const initialTheme = memberTheme || storedTheme || "light";

      setThemeState(initialTheme);
      applyTheme(initialTheme);
    };

    initializeTheme();
  }, [member?.settings?.theme]);

  // Apply theme to document root
  const applyTheme = (newTheme: Theme) => {
    const root = document.documentElement;

    if (newTheme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    // Store in localStorage for persistence across sessions
    localStorage.setItem("theme", newTheme);
  };

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    applyTheme(newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
